import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { toast } from '@shopee/chatbot-ui';
import clsx from 'classnames';
import {
  BaseUpload,
  BaseUploadCard,
  FileType,
  SelectType,
  UploadFile,
  UploadStatus,
} from '@shopee/chatbot-ui/lib/components/BaseUpload';
import {
  handleSelectFile as handleSelectFileImpl,
  handleGallerySelect as handleGallerySelectImpl,
  handleSelectPhotoLibrary as handleSelectPhotoLibraryImpl,
  SUPPORT_FILE_TYPE,
} from '@/utils/fileSelectHandler';
import { getTrans } from '@/utils/instantLoaded/tools';
import { getImageFileFromBase64, openFullScreenImage, replaceHttpLinkWithHttps } from '@/utils/tools';
import { getVideoToken } from '@/services/chat';
import { Copywriting, MediaSourceType, MediaType } from '@/utils/constants';
import initStore from '@/stores/init';
import { addExtraParamsForPlayVideo, formatMediaTokens, playVideo, startMediaUpload } from '@/utils/videoTools';
import { addMediasListener, removeMediasListener } from '@/utils/agent';
import { MediaBroadcast, UploadStatus as VideoUploadStatus } from '@/types/components/Video';
import { FileUploadWrapper } from './wrapper/FileUploadWrapper';
import { ImageUploadWrapper } from './wrapper/ImageUploadWrapper';
import { formatDuration, VideoUploadProps, VideoUploadWrapper } from './wrapper/VideoUploadWrapper';
import { getConfig } from '@/config/core';
import { previewWithBridge } from '@/utils/previewWithBridge';
import reporter from '@/utils/report';
import './style.scss';

interface ProbingFormUploadFile extends UploadFile {
  videoId?: string;
  customFileProps?: { file: File };
  customVideoProps?: Omit<VideoUploadProps, 'onRemove' | 'onPreview'>;
}
export interface ProbingFormFile {
  name?: string;
  url?: string;
  type: FileType;
  attributes?: {
    cover_url?: string;
    video_duration?: number;
  }
}
export interface ProbingFormUploadProps {
  fileList: ProbingFormFile[];
  onChange?: (fileList: ProbingFormFile[]) => void;
  isEditing?: boolean;
}

const MAX_COUNT = 5;

export function ProbingFormUpload(props: ProbingFormUploadProps) {
  const [fileList, _setFileList] = useState<(ProbingFormUploadFile | null)[]>(toFileList(props.fileList || []));

  const fileListRef = useRef(fileList);
  const setFileList = (list: (UploadFile | null)[]) => {
    _setFileList(list);
    fileListRef.current = list;
  };


  const onSuccess = (url: string, index: number) => {
    fileList[index]!.url = url;
    setFileList([...fileList]);
    handleChange(fileList);
  };

  const onRemove = (index: number) => {
    fileList[index] = null;
    setFileList([...fileList]);
    handleChange(fileList);
  };

  const handleChange = (fileList: (UploadFile | null)[]) => {
    const saveList = fileList.filter((f) => f != null);
    const probingFormFileList = (saveList as UploadFile[]).map((f) => {
      const fileInfo = { name: f.name ?? '', url: f.url, type: f.type };
      if (f.type === FileType.Video) {
        return { ...fileInfo, attributes: { cover_url: f.coverUrl, video_duration: f.duration }};
      }
      return fileInfo;
    });

    props.onChange?.(probingFormFileList as ProbingFormFile[]);
  };

  const checkAndShowExceedToast = (count: number) => {
    const currentCount = fileList.filter((f) => f != null).length;
    if (currentCount + count > 5) {
      toast.error(getTrans('probing_form_exceeds_maximum'));
      return true;
    }

    return false;
  };

  const handleSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (checkAndShowExceedToast(1)) {
      return;
    }

    handleSelectFileImpl(e, (file) => {
      fileList.push({
        type: FileType.File,
        name: file.name,
        customFileProps: { file },
      });
      setFileList([...fileList]);
      handleChange(fileList);
    });
  };

  const appendImageFile = (file: File) => {
    fileList.push({
      type: FileType.Image,
      customFileProps: { file: getImageFileFromBase64(file) as File },
    });
    setFileList([...fileList]);
    handleChange(fileList);
  };

  const handlePhotoLibrarySelect = () => {
    handleSelectPhotoLibraryImpl((images: File[]) => {
      checkAndShowExceedToast(images.length);

      const currentCount = fileList.filter((f) => f != null).length;
      images.slice(0, MAX_COUNT - currentCount).forEach((images) => {
        fileList.push({
          type: FileType.Image,
          customFileProps: { file: getImageFileFromBase64(images) as File },
        });
      });
      setFileList([...fileList]);
      handleChange(fileList);
    }, gallerySelectCallback);
  };


  const batchUpdateVideoStatus = (ids: string[]) => {
    setFileList(
      fileList.map((f) => {
        if (f?.type === FileType.Video && f.status === UploadStatus.Loading) {
          if (ids.includes(f.videoId!)) {
            return { ...f, status: UploadStatus.Fail };
          }
        }

        return f;
      }),
    );
  };

  const gallerySelectCallback = (data: any) => {
    checkAndShowExceedToast(data.length);

    const currentCount = fileList.filter((f) => f != null).length;
    const leftData = data.slice(0, MAX_COUNT - currentCount);

    const videoIds: string[] = [];

    leftData.forEach((dataItem: any) => {
      if (dataItem.type === MediaType.Image) {
        fileList.push({
          type: FileType.Image,
          customFileProps: { file: getImageFileFromBase64(dataItem.image) as File },
        });
      } else if (dataItem.type === MediaType.Video) {
        fileList.push({
          videoId: dataItem.id,
          type: FileType.Video,
          coverUrl: dataItem.videoCover,
          duration: dataItem.videoDuration,
          customVideoProps: {
            status: UploadStatus.Loading,
            coverUrl: dataItem.videoCover,
            duration: dataItem.videoDuration,
          },
        });
        videoIds.push(dataItem.id);
        reporter.apiBatchReport({ type: 'count', value: { label: 'probing_form_upload_video_total' }});
      }
    });

    setFileList([...fileList]);
    handleChange(fileList);

    const videoCount = (leftData || []).filter((i: any) => i.type === MediaType.Video).length;

    if (videoCount > 0) {
      getVideoToken(initStore.sessionId, videoCount)
        .then((res) => {
          if (!(res instanceof Error)) {
            const formatTokens = formatMediaTokens(res.data);
            videoIds.forEach((videoId: string, index) => {
              // video native 上传
              startMediaUpload(videoId, formatTokens[index]);
            });
          } else {
            batchUpdateVideoStatus(videoIds);
          }
        })
        .catch(() => {
          batchUpdateVideoStatus(videoIds);
        });
    }
  };

  const handleGallerySelect = () => {
    handleGallerySelectImpl(MediaSourceType.AlbumAndCamera, (data, newEntry) => {
      if (!newEntry) {
        if (checkAndShowExceedToast(1)) {
          return;
        }

        appendImageFile(data.image);
        return;
      }

      gallerySelectCallback(data);
    });
  };

  useEffect(() => {
    addMediasListener();
    window.bridgeRegisterHandler('MEDIA_SELECT_UPLOAD_PROGRESS_EVENT', (mediaBroadcast: MediaBroadcast) => {
      const { id, ...restMediaInfo } = mediaBroadcast;

      const fileList = fileListRef.current;
      const file = fileList.find((f) => f?.videoId === id);
      const index = fileList.findIndex((f) => f?.videoId === id);
      if (file != null) {
        if (restMediaInfo.status === VideoUploadStatus.UploadSucceed) {
          file.status = UploadStatus.Success;
          file.url = replaceHttpLinkWithHttps(restMediaInfo.videoUrl);
          file.coverUrl = restMediaInfo.videoCoverUrl;
          file.customVideoProps = {
            ...file.customVideoProps!,
            status: UploadStatus.Success,
          };
          reporter.apiBatchReport({ type: 'count', value: { label: 'probing_form_upload_video_success' }});
        } else if (
          restMediaInfo.status === VideoUploadStatus.UploadFailed
          || restMediaInfo.status === VideoUploadStatus.CompressFailed
        ) {
          toast.error(getTrans('probing_form_submit_failed'));
          fileList[index] = null;
          reporter.apiLog(
            `【Probing Form Upload】video upload failed, errormsg:${JSON.stringify(mediaBroadcast)}`,
            { level: 'error' },
          );
        }

        setFileList([...fileList]);
        handleChange(fileList);
      }
    });

    return () => {
      removeMediasListener();
    };
  }, []);

  useEffect(() => {
    // 只读状态时，fileList接受更新
    if (!props.isEditing) {
      setFileList(toFileList(props.fileList || []));
    }
  }, [props.fileList, props.isEditing]);

  const handleUpload = (type: SelectType) => {
    if (type === SelectType.File) {
      const inputFile = document.querySelector<HTMLInputElement>('#probing-form-input')!;
      inputFile.value = null as unknown as string;
      inputFile!.click();
    } else if (type === SelectType.Photos) {
      handlePhotoLibrarySelect();
    } else if (type === SelectType.Gallery) {
      handleGallerySelect();
    }
  };

  const handlePreview = (index: number) => {
    const url = fileList[index]?.url;

    if (!url) {
      return;
    }

    const type = fileList[index]!.type;
    if (type === FileType.File) {
      const config = getConfig();
      const version = window.getAppVersion();

      if (version >= config?.fileUploadVersion && url) {
        previewWithBridge(url, fileList[index]!.name!);
      } else {
        toast.error(getTrans(Copywriting.FILE_PREVIEW_IN_OLD_VERSION));
      }
    } else if (type === FileType.Image) {
      openFullScreenImage(url);
    } else if (type === FileType.Video) {
      const playUrl = replaceHttpLinkWithHttps(addExtraParamsForPlayVideo(url));
      playVideo(playUrl);
    }
  };

  const nonEmptyFileList = fileList.filter((f) => f != null) as ProbingFormUploadFile[];

  return (
    <>
      {nonEmptyFileList.length !== 0 && (
        <div className={clsx('probing-form-card-list', { editing: props.isEditing })}>
          {fileList.map((v, index) => {
            if (!v) {
              return null;
            }

            if (v.customFileProps == null && v.customVideoProps == null) {
              return (
                <BaseUploadCard
                  file={v}
                  onRemove={() => onRemove(index)}
                  onPreview={() => handlePreview(index)}
                  showRemoveIcon={props.isEditing}
                />
              );
            }

            if (v?.type === FileType.File) {
              return (
                <FileUploadWrapper
                  {...v.customFileProps!}
                  onSuccess={(url: string) => onSuccess(url, index)}
                  onRemove={() => onRemove(index)}
                  onPreview={() => handlePreview(index)}
                  showRemoveIcon={props.isEditing}
                />
              );
            } else if (v.type === FileType.Image) {
              return (
                <ImageUploadWrapper
                  {...v.customFileProps!}
                  onSuccess={(url: string) => onSuccess(url, index)}
                  onRemove={() => onRemove(index)}
                  onPreview={() => handlePreview(index)}
                  showRemoveIcon={props.isEditing}
                />
              );
            } else if (v.type === FileType.Video) {
              return (
                <VideoUploadWrapper
                  {...v.customVideoProps!}
                  onRemove={() => onRemove(index)}
                  onPreview={() => handlePreview(index)}
                  showRemoveIcon={props.isEditing}
                />
              );
            }
          })}
        </div>
      )}
      {props.isEditing && (
        <>
          <BaseUpload maxCount={MAX_COUNT} fileList={nonEmptyFileList} handleUpload={handleUpload} />
          <input
            id="probing-form-input"
            type="file"
            onChange={handleSelectFile}
            accept={SUPPORT_FILE_TYPE.join(', ')}
            style={{ display: 'none' }}
          />
        </>
      )}
    </>
  );
}

function toFileList(fileList: ProbingFormFile[]): UploadFile[] {
  return fileList.map((f) => {
    return {
      ...f,
      coverUrl: f.attributes?.cover_url,
      duration: f.attributes?.video_duration ? formatDuration(f.attributes?.video_duration) : undefined,
    };
  });
}
